import React from 'react';
import { dispatch, dispatchSync, useReframe } from 'nike-re-framejs';

import { clearBetslip, clearSubmit } from '@/betslip/commands';
import BetslipUserAliasDialog from '@/betslip/views/BetslipUserAliasDialog';
import { handleDispatch, isScoreboardError } from '@/common/client';
import { PopupButton } from '@/common/components/Button';
import i18n from '@i18n';
import { Popup } from './Popup';
import { TICKET_DETAIL } from '@/routing/routes';
import { updateURLSearchParams, navigate } from '@/routing';
import { loggedIn$ } from '@/account/subs';
import { savedTicketParams$ } from '@/mytickets/saved/subs';
import { enabledNewConflictsModal } from 'nike-ui/src/components/betslip/conflicting-bets-dialog';

function ErrorButtons({ commandList }) {
    return commandList.isEmpty() ? (
        <div className="actions">
            <PopupButton onClick={handleDispatch(['confirmCmdError'])} i18nCode="jsp.message.zavriet" />
        </div>
    ) : (
        <div className="actions">
            {commandList.map((cmd, i) => (
                <PopupButton
                    key={i}
                    message={cmd.get('label')}
                    onClick={handleDispatch(['validationCmd', cmd.get('cmd')])}
                />
            ))}
        </div>
    );
}

function Submitting({ bookmakerApproving }) {
    const code = bookmakerApproving ? 'liveStavky.tiket.msg.bookmakerApproving' : 'liveStavky.tiket.msg.delayed';
    return (
        <Popup>
            <div className="ajax-preloader" />
            <br />
            <p className="text-center">{i18n(code)}</p>
            <div className="actions" />
        </Popup>
    );
}

/**
 *
 * DISCLAIMER: kazdy kto chce editovat tento komponent musi mat jasno v nasledujucom:
 *
 * Popup sa zobrazi ak je tiket v stave 'podany' a zaroven:
 * 1. nie je otvoreny tiket editor (!isBetslipOpen) alebo
 * 2. pouzivatel zaskrtol ponechat tipy na tikete (shouldKeepBets)
 *
 *
 * narocnejsie je, co sa ma stat pri zatvoreni popupu:
 *
 * z pohladu betslipu:
 * 1. mimo tiket editora sa musi 'vzdy' ack-nut submit aj clear-nut tiket (clearSubmit+clearBetslip)
 * 2. v tiket editore ak su ponechane tipy (shouldKeepBets) tak len clearSubmit
 * 3. v tiket editore ak nie su ponechane tipy -> nestane sa nic
 *
 */

const SubmitSuccessPopup = React.memo(function SubmitSuccessPopup({
    isBetslipOpen,
    shouldKeepBets,
    publishTicketHash
}) {
    const { derefSub } = useReframe();

    const onBackdrop = e => {
        finishBetslipSubmit(e);
    };
    const clearBetslipAndClearSubmit = () => {
        // TODO toto je chujovina, treba do betslipu novy command ktory to vykona naraz
        dispatchSync(clearSubmit());
        dispatch(clearBetslip());
    };
    const finishBetslipSubmit = () => {
        if (!isBetslipOpen) {
            clearBetslipAndClearSubmit();
            return;
        }
        if (shouldKeepBets) {
            dispatchSync(clearSubmit(shouldKeepBets));
        } else {
            clearBetslipAndClearSubmit();
            updateURLSearchParams({ tiket: undefined });
        }
    };
    const goToTicketDetail = e => {
        finishBetslipSubmit(e);
        navigate(TICKET_DETAIL, { hash: publishTicketHash });
    };

    const isSubmittedWithChangedOdds = derefSub(['betslip/isSubmittedWithChangedOdds']);

    return (
        <Popup className="success2" onClose={onBackdrop}>
            <h1 className="breakable">{i18n('liveStavky.tiket.msg.slipPlaced')}</h1>
            <p>
                {isSubmittedWithChangedOdds
                    ? i18n('liveStavky.tiket.msg.slipPlaced3')
                    : i18n('liveStavky.tiket.msg.slipPlaced2')}
            </p>
            <div className="actions">
                <PopupButton onClick={finishBetslipSubmit} i18nCode="office.filter.button" />
                <PopupButton onClick={goToTicketDetail} i18nCode="commons.button.zobrazit" />
            </div>
        </Popup>
    );
});

function SubmitError({ cmdError }) {
    return (
        <Popup>
            <p>{i18n('react.betslip.submit.failed') + ' ' + cmdError.get('localizedType')}</p>
            <ErrorButtons commandList={cmdError.get('commands')} />
        </Popup>
    );
}

function ScoreboardError({ cmdError }) {
    return (
        <Popup className="warn">
            <h1 className="icon">{i18n('popup.info')}</h1>
            <p>{cmdError.get('localizedType')}</p>
            <ErrorButtons commandList={cmdError.get('commands')} />
        </Popup>
    );
}

function BetslipPopup() {
    const derefSub = useReframe();
    const showSubmitLoader = derefSub(['betslip/showSubmitLoader']);

    if (showSubmitLoader) {
        const isBookmakerApproving = derefSub(['betslip/isBookmakerApproving']);
        return <Submitting bookmakerApproving={isBookmakerApproving} />;
    }

    const isSubmitted = derefSub(['betslip/isSubmitted']);
    if (isSubmitted) {
        const publishTicketHash = derefSub(['betslip/publishTicketHash']);
        const shouldKeepBets = derefSub(['betslip/submittedWithKeptBets']);
        const isBetslipOpen = derefSub(['betslip/isOpen']);

        return (
            <SubmitSuccessPopup
                shouldKeepBets={shouldKeepBets}
                isBetslipOpen={isBetslipOpen}
                publishTicketHash={publishTicketHash}
            />
        );
    }
    const cmdError = derefSub(['betslip/lastCmdError']);
    if (cmdError) {
        const isBetslipOpen = derefSub(['betslip/isOpen']);
        if ((cmdError.get('submitError') || cmdError.get('type') === 'SUBMIT_ERROR') && !isBetslipOpen) {
            return <SubmitError cmdError={cmdError} />;
        } else if (isScoreboardError(cmdError.get('type')) && !enabledNewConflictsModal(cmdError)) {
            return <ScoreboardError cmdError={cmdError} />;
        }
    }
    return null;
}

function SavedBetslipPopup() {
    const { derefSub, subscribe } = useReframe();
    const savedBetslipParams = derefSub(['betslip/savedBetslipParams']);
    const ticketusSavedParams = derefSub(savedTicketParams$);
    const loggedIn = subscribe(loggedIn$);

    if (!savedBetslipParams && !ticketusSavedParams) {
        return null;
    }
    if (loggedIn) {
        return <BetslipUserAliasDialog />;
    }

    const onClose = () => dispatch(clearSubmit());

    const open = () => {
        navigate(TICKET_DETAIL, {
            hash: savedBetslipParams.get('betslipUrl')
        });
        dispatch(clearSubmit());
    };
    return (
        <Popup onClose={onClose}>
            <h1>{i18n('tiket.stored.info.title')}</h1>
            <p>
                {i18n('tiket.stored.info.text')}&nbsp;
                <strong className="text-nowrap">{savedBetslipParams.get('betslipNumber')}</strong>
            </p>
            <p className="small">{i18n('tiket.stored.info.small')}</p>
            <div className="actions">
                <span data-atid="full-ticket-saved-open">
                    <PopupButton onClick={open} i18nCode="tiket.stored.info.open" />
                </span>
                <span data-atid="full-ticket-saved-close">
                    <PopupButton onClick={onClose} i18nCode="jsp.message.zavriet" />
                </span>
            </div>
        </Popup>
    );
}

function BetslipPopups() {
    const { derefSub } = useReframe();
    const shouldShow = derefSub(['betslip/popup/shouldShowGuard']);
    const savedTicketParams = derefSub(savedTicketParams$);
    if (!shouldShow && !savedTicketParams) {
        return null;
    }
    return (
        <>
            <BetslipPopup />
            <SavedBetslipPopup />
        </>
    );
}

export default BetslipPopups;
